
import { useState, useEffect } from 'react';

interface TodoItem {
  id: string;
  text: string;
  category: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
}

const PROJECT_ISSUES_CHECKLIST: Omit<TodoItem, 'id' | 'completed' | 'createdAt'>[] = [
  // Critical Issues
  { text: 'Check all dependencies are properly imported', category: 'Critical', priority: 'critical' },
  { text: 'Verify no route conflicts exist', category: 'Critical', priority: 'critical' },
  { text: 'Test authentication login/logout flow', category: 'Critical', priority: 'critical' },
  { text: 'Validate all API endpoints are correct', category: 'Critical', priority: 'critical' },
  { text: 'Fix all TypeScript errors', category: 'Critical', priority: 'critical' },

  // Design & Styling
  { text: 'Establish unified color system', category: 'Design', priority: 'high' },
  { text: 'Remove inline styles, use CSS classes', category: 'Design', priority: 'high' },
  { text: 'Test responsive design on mobile', category: 'Design', priority: 'medium' },
  { text: 'Verify custom fonts load properly', category: 'Design', priority: 'medium' },
  { text: 'Use consistent icon library throughout', category: 'Design', priority: 'medium' },

  // Structure & Organization
  { text: 'Organize components in proper folders', category: 'Structure', priority: 'high' },
  { text: 'Remove duplicate code across files', category: 'Structure', priority: 'medium' },
  { text: 'Clean up unused imports', category: 'Structure', priority: 'low' },
  { text: 'Use consistent naming conventions', category: 'Structure', priority: 'medium' },
  { text: 'Reduce component coupling', category: 'Structure', priority: 'medium' },

  // Performance
  { text: 'Optimize bundle size', category: 'Performance', priority: 'medium' },
  { text: 'Clean up event listeners', category: 'Performance', priority: 'high' },
  { text: 'Add React optimization (memo, callback)', category: 'Performance', priority: 'medium' },
  { text: 'Compress and optimize images', category: 'Performance', priority: 'low' },
  { text: 'Implement lazy loading for components', category: 'Performance', priority: 'medium' },

  // Security & Data
  { text: 'Move API keys to environment variables', category: 'Security', priority: 'critical' },
  { text: 'Sanitize all user inputs', category: 'Security', priority: 'high' },
  { text: 'Configure CORS properly', category: 'Security', priority: 'high' },
  { text: 'Add input validation on forms', category: 'Security', priority: 'high' },
  { text: 'Secure all protected routes', category: 'Security', priority: 'critical' },

  // Testing & Quality
  { text: 'Add error boundaries to components', category: 'Quality', priority: 'high' },
  { text: 'Implement loading states', category: 'Quality', priority: 'medium' },
  { text: 'Add proper error handling', category: 'Quality', priority: 'high' },
  { text: 'Include ARIA labels for accessibility', category: 'Quality', priority: 'medium' },
  { text: 'Test browser compatibility', category: 'Quality', priority: 'low' },

  // State Management
  { text: 'Avoid direct state mutations', category: 'State', priority: 'high' },
  { text: 'Minimize prop drilling', category: 'State', priority: 'medium' },
  { text: 'Use global state appropriately', category: 'State', priority: 'medium' },
  { text: 'Fix useEffect dependencies', category: 'State', priority: 'high' },
  { text: 'Handle async race conditions', category: 'State', priority: 'medium' },

  // User Experience
  { text: 'Add loading indicators', category: 'UX', priority: 'medium' },
  { text: 'Improve error messages', category: 'UX', priority: 'medium' },
  { text: 'Add offline support', category: 'UX', priority: 'low' },
  { text: 'Enhance form validation messages', category: 'UX', priority: 'medium' },
  { text: 'Improve navigation flow', category: 'UX', priority: 'medium' },

  // Development
  { text: 'Set up environment variables', category: 'Development', priority: 'high' },
  { text: 'Test production build', category: 'Development', priority: 'high' },
  { text: 'Verify hot reload works', category: 'Development', priority: 'low' },
  { text: 'Check package compatibility', category: 'Development', priority: 'medium' },
  { text: 'Set up proper Git workflow', category: 'Development', priority: 'medium' },

  // Data & API
  { text: 'Verify HTTP methods and headers', category: 'API', priority: 'high' },
  { text: 'Implement proper caching strategy', category: 'API', priority: 'medium' },
  { text: 'Fix pagination functionality', category: 'API', priority: 'medium' },
  { text: 'Optimize search performance', category: 'API', priority: 'low' },
  { text: 'Test real-time updates', category: 'API', priority: 'low' }
];

export default function TodoList() {
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [newTodo, setNewTodo] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Custom');
  const [selectedPriority, setSelectedPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');

  useEffect(() => {
    const savedTodos = localStorage.getItem('project-todos');
    if (savedTodos) {
      setTodos(JSON.parse(savedTodos));
    } else {
      // Initialize with project issues checklist
      const initialTodos = PROJECT_ISSUES_CHECKLIST.map(item => ({
        ...item,
        id: `todo-${Date.now()}-${Math.random()}`,
        completed: false,
        createdAt: new Date().toISOString()
      }));
      setTodos(initialTodos);
      localStorage.setItem('project-todos', JSON.stringify(initialTodos));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('project-todos', JSON.stringify(todos));
  }, [todos]);

  const addTodo = () => {
    if (newTodo.trim()) {
      const todo: TodoItem = {
        id: `todo-${Date.now()}-${Math.random()}`,
        text: newTodo.trim(),
        category: selectedCategory,
        priority: selectedPriority,
        completed: false,
        createdAt: new Date().toISOString()
      };
      setTodos([...todos, todo]);
      setNewTodo('');
    }
  };

  const toggleTodo = (id: string) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const deleteTodo = (id: string) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const resetChecklist = () => {
    const resetTodos = PROJECT_ISSUES_CHECKLIST.map(item => ({
      ...item,
      id: `todo-${Date.now()}-${Math.random()}`,
      completed: false,
      createdAt: new Date().toISOString()
    }));
    setTodos(resetTodos);
  };

  const filteredTodos = todos.filter(todo => {
    const statusMatch = filter === 'all' || 
      (filter === 'completed' && todo.completed) || 
      (filter === 'pending' && !todo.completed);
    
    const categoryMatch = categoryFilter === 'all' || todo.category === categoryFilter;
    
    return statusMatch && categoryMatch;
  });

  const categories = ['all', ...Array.from(new Set(todos.map(todo => todo.category)))];
  const completedCount = todos.filter(todo => todo.completed).length;
  const totalCount = todos.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'Critical': 'bg-red-50 border-l-red-500',
      'Design': 'bg-purple-50 border-l-purple-500',
      'Structure': 'bg-blue-50 border-l-blue-500',
      'Performance': 'bg-green-50 border-l-green-500',
      'Security': 'bg-red-50 border-l-red-600',
      'Quality': 'bg-indigo-50 border-l-indigo-500',
      'State': 'bg-cyan-50 border-l-cyan-500',
      'UX': 'bg-pink-50 border-l-pink-500',
      'Development': 'bg-gray-50 border-l-gray-500',
      'API': 'bg-teal-50 border-l-teal-500',
      'Custom': 'bg-amber-50 border-l-amber-500'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-50 border-l-gray-500';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Project Issues Checklist</h1>
          <p className="text-gray-600 mt-1">Track and prevent common project problems</p>
        </div>
        <button
          onClick={resetChecklist}
          className="bg-[var(--color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          Reset Checklist
        </button>
      </div>

      {/* Progress Overview */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Progress Overview</h3>
          <span className="text-sm text-gray-600">{completedCount} of {totalCount} completed</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-[var(--color-primary)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">{progressPercentage.toFixed(1)}% Complete</p>
      </div>

      {/* Add New Todo */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Add Custom Task</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Enter new task..."
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && addTodo()}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
          >
            <option value="Custom">Custom</option>
            <option value="Critical">Critical</option>
            <option value="Design">Design</option>
            <option value="Structure">Structure</option>
            <option value="Performance">Performance</option>
            <option value="Security">Security</option>
            <option value="Quality">Quality</option>
            <option value="State">State</option>
            <option value="UX">UX</option>
            <option value="Development">Development</option>
            <option value="API">API</option>
          </select>
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value as any)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <button
            onClick={addTodo}
            className="bg-[var(--color-primary)] text-white px-6 py-3 rounded-lg hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap cursor-pointer"
          >
            Add Task
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="flex gap-2">
            <span className="text-sm font-medium text-gray-700 self-center">Status:</span>
            {['all', 'pending', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  filter === status
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <span className="text-sm font-medium text-gray-700 self-center">Category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Todo List */}
      <div className="space-y-3">
        {filteredTodos.map(todo => (
          <div
            key={todo.id}
            className={`bg-white p-4 rounded-lg shadow border-l-4 ${getCategoryColor(todo.category)} ${
              todo.completed ? 'opacity-75' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={todo.completed}
                onChange={() => toggleTodo(todo.id)}
                className="mt-1 w-5 h-5 text-[var(--color-primary)] border-gray-300 rounded focus:ring-[var(--color-primary)] cursor-pointer"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(todo.priority)}`}>
                    {todo.priority.toUpperCase()}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                    {todo.category}
                  </span>
                </div>
                <p className={`text-gray-900 ${todo.completed ? 'line-through text-gray-500' : ''}`}>
                  {todo.text}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Added: {new Date(todo.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => deleteTodo(todo.id)}
                className="text-red-500 hover:text-red-700 p-1 cursor-pointer"
                title="Delete task"
              >
                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTodos.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <i className="ri-checkbox-circle-line text-4xl text-gray-400 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
          <p className="text-gray-500">No tasks found for the selected filters.</p>
        </div>
      )}
    </div>
  );
}
